import Reference, {ReferenceAttributesSettable} from '~/shared/src/ci/Reference';

import {DATA_UPDATE_REFERENCE} from '~/frontend/src/constants';
import {AppDispatch, StoreState, Thunk} from '~/frontend/src/store';
import {setElusiveData} from '~/frontend/src/actions/elusiveData';


export type ReferenceUpdate = {
    referenceId : number;
    attributes : ReferenceAttributesSettable;
    additionalElusiveDataContexts? : string[];
};

/**
 * Bulk variant of updateReference. Processes N reference updates and emits
 * exactly one setElusiveData per affected elusiveData context with all
 * item / reference increments merged.
 */
const updateReferences = ( dataContext : string, updates : ReferenceUpdate[] ) : Thunk<void> =>
    ( dispatch : AppDispatch, getState : () => StoreState ) => {
        if ( !updates.length ) {
            return;
        }

        const itemIncrements : { [ ctx : string ] : { [ id : number ] : number } } = {},
              refIncrements  : { [ ctx : string ] : { [ id : number ] : number } } = {};

        for ( const { referenceId, attributes, additionalElusiveDataContexts = [] } of updates ) {

            dispatch( {
                type: DATA_UPDATE_REFERENCE,
                dataContext,
                referenceId,
                attributes,
                additionalElusiveDataContexts
            } );

            const reference : Reference                           = global.data[ dataContext ]?.references[ referenceId ],
                  attributesNames                                 = Object.keys( attributes ),
                  changedAttributes : ReferenceAttributesSettable = {};

            if ( !reference || !attributesNames.length ) {
                continue;
            }

            for ( const attributeName of attributesNames ) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                if ( reference[ attributeName ] !== attributes[ attributeName ] ) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    changedAttributes[ attributeName ] = attributes[ attributeName ];
                }
            }

            if ( !Object.keys( changedAttributes ).length ) {
                continue;
            }

            reference.clone( changedAttributes, true );

            for ( const ctx of [ dataContext, ...additionalElusiveDataContexts ] ) {
                ( itemIncrements[ ctx ] ??= {} );
                ( refIncrements[ ctx ]  ??= {} );

                itemIncrements[ ctx ][ reference.srcItemId ]  = ( itemIncrements[ ctx ][ reference.srcItemId ]  ?? 0 ) + 1;
                itemIncrements[ ctx ][ reference.destItemId ] = ( itemIncrements[ ctx ][ reference.destItemId ] ?? 0 ) + 1;
                refIncrements[ ctx ][ reference.id ]          = ( refIncrements[ ctx ][ reference.id ]          ?? 0 ) + 1;
            }
        }

        const itemCtxKeys           = Object.keys( itemIncrements ) as string[],
              refCtxKeys            = Object.keys( refIncrements ) as string[],
              contexts : string[]   = Array.from( new Set( [ ...itemCtxKeys, ...refCtxKeys ] ) );

        for ( const ctx of contexts ) {
            const elusiveData = getState().elusiveData?.[ ctx ],
                  items       = { ...elusiveData?.data?.items },
                  references  = { ...elusiveData?.data?.references };

            for ( const idStr of Object.keys( itemIncrements[ ctx ] ?? {} ) ) {
                const id = Number( idStr );
                items[ id ] = ( items[ id ] ?? 0 ) + itemIncrements[ ctx ][ id ];
            }
            for ( const idStr of Object.keys( refIncrements[ ctx ] ?? {} ) ) {
                const id = Number( idStr );
                references[ id ] = ( references[ id ] ?? 0 ) + refIncrements[ ctx ][ id ];
            }

            dispatch( setElusiveData( ctx, {
                ...elusiveData,
                data: {
                    ...elusiveData?.data,
                    items,
                    references
                }
            } ) );
        }
    };

export type UpdateReferences = typeof updateReferences;
export default updateReferences;